/**
 * The Height breach report's entries of a project on its Safety tab (R197, decision 1017; docs/specs/geosoft-qc.md
 * §28; help `cloud.safety-documents`). Entries are added on Teslyn Field from Flight data QC and synced; here the
 * project's staff read them, nobody changes them: each breach's facts as the report writes them, the reason (and the
 * reasons it replaced), who added it and when, and its chart. Removed entries are listed after, with why. Clients
 * never see this section (staff only; RLS keeps the rows and charts from them).
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  canReadHeightBreaches,
  heightBreachEntryLines,
  heightBreachesForReading,
  type HeightBreachEntry,
} from '@teslyn/domain';
import { ErrorState, Loading } from '../components/States.tsx';
import type { PortalProject } from '../portal/api.ts';
import {
  breachChartUrl,
  loadProjectHeightBreaches,
  type ProjectHeightBreaches,
} from './heightBreachesApi.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** "4 Nov 2026" in the browser's own time. */
const day = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

function Entry({
  sb,
  entry,
  chart,
}: {
  readonly sb: SupabaseClient;
  readonly entry: HeightBreachEntry;
  readonly chart: string | undefined;
}): ReactElement {
  const [url, setUrl] = useState<string | null>(null);
  const [chartError, setChartError] = useState<string | null>(null);
  useEffect(() => {
    if (!chart) return;
    let live = true;
    breachChartUrl(sb, chart)
      .then((u) => live && setUrl(u))
      .catch((e: unknown) => live && setChartError(message(e)));
    return () => {
      live = false;
    };
  }, [sb, chart]);
  const lines = heightBreachEntryLines(entry.facts);
  const removed = entry.deleted_at !== null;
  return (
    <li className="stack" aria-label={`Breach of ${entry.line_name} at zFid ${entry.fid_start}`}>
      <div className="data caption">
        {lines.map((l) => (
          <p key={l}>{l}</p>
        ))}
      </div>
      <p className="body">Reason – {entry.reason}</p>
      <p className="caption muted">
        Added by {entry.created_by_name || 'a laptop of the project'} on {day(entry.created_at)}.
        {entry.history.length > 0 &&
          ` Reason changed ${entry.history.length} ${entry.history.length === 1 ? 'time' : 'times'}; before: ${entry.history
            .map((h) => `“${h.reason}”`)
            .join(', ')}.`}
      </p>
      {removed && (
        <p className="caption">
          Removed from the report by {entry.deleted_by_name || 'a laptop of the project'} on{' '}
          {day(entry.deleted_at!)}: {entry.deleted_reason}
        </p>
      )}
      {!chart && (
        <p className="caption muted">
          The chart is not in Teslyn Cloud yet: the laptop sends it at its next sync.
        </p>
      )}
      {chartError && (
        <p className="caption muted" role="alert">
          The chart could not be shown. {chartError}
        </p>
      )}
      {url && (
        <img className="breach-chart" src={url} alt={`Heights of ${entry.line_name} with the breach`} />
      )}
    </li>
  );
}

/** Staff: every entry of the project's Height breach report, read only. */
export function StaffHeightBreaches({
  sb,
  project,
  roles,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly roles: readonly string[];
}): ReactElement | null {
  const [data, setData] = useState<ProjectHeightBreaches | null | 'missing'>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(() => {
    setError(null);
    loadProjectHeightBreaches(sb, project.id)
      .then((d) => setData(d ?? 'missing'))
      .catch((e: unknown) => setError(message(e)));
  }, [sb, project.id]);
  useEffect(reload, [reload]);

  if (!canReadHeightBreaches(roles)) return null;
  // the database update with the entries is not applied yet: nothing to say
  if (data === 'missing') return null;
  const title = 'Height breach report';
  if (error)
    return (
      <section className="panel stack" aria-label={title}>
        <h2 className="heading">{title}</h2>
        <ErrorState message={error} onRetry={reload} />
      </section>
    );
  if (!data) return <Loading what="Height breach report" />;
  const { current, removed } = heightBreachesForReading(data.entries);
  return (
    <section className="panel stack" aria-label={title}>
      <h2 className="heading">{title}</h2>
      <p className="caption muted">
        The breaches of the lower working height and the hard deck recorded in Teslyn Field (Flight data QC,
        Breach report), with the reason given. Read only here; the Word document and PDF are in the project
        folder. Staff only: the client does not see them.
      </p>
      {current.length === 0 ? (
        <p className="body muted">No breach in the report.</p>
      ) : (
        <ul className="stack" aria-label="Breaches in the report">
          {current.map((e) => (
            <Entry key={e.id} sb={sb} entry={e} chart={data.charts[e.id]} />
          ))}
        </ul>
      )}
      {removed.length > 0 && (
        <div className="stack">
          <h3 className="label">Removed from the report</h3>
          <ul className="stack" aria-label="Removed from the report">
            {removed.map((e) => (
              <Entry key={e.id} sb={sb} entry={e} chart={data.charts[e.id]} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
